// AuthContext shim — match interface useAuth() của Core (attendance-app), backed bởi zustand authStore của SaaS.
// Components copy từ Core dùng `useAuth()` sẽ chạy được mà KHÔNG cần đổi.
//
// Core's useAuth() trả: { user, loading, login, logout, loginWithGoogle, setPreference, ... }
// Mình map các field dùng nhiều + setPreference (Core MobileSection cần — TypeError nếu thiếu).

import { createContext, useContext, useEffect } from 'react';
import { useAuthStore } from '../lib/authStore';
import { inboxClient, authApi, resetAuthBroken, baseURL } from '../lib/inboxClient';
import { unsubscribeWebPush } from '../lib/webPushLogout';
import { applyServerLocale, getLocale } from '@cnv/ui/i18n';
import { syncDeviceTimezone } from '../lib/deviceTimezone';

const AuthCtx = createContext(null);

// Máy chủ vừa NHẬN múi máy của chủ/admin làm múi workspace (PUT /auth/me/timezone › companyTimezoneAdoption — workspace
// còn giờ VN gieo sẵn) ⇒ cập nhật activeTenant để web đổi múi hiển thị ngay (lib/workspaceTimezone.js), không đợi F5.
function apMuiWorkspaceVuaNhan(res) {
  if (!res?.company_timezone_adopted || !res.company_timezone) return;
  const cur = useAuthStore.getState().activeTenant;
  if (!cur || cur.company_timezone === res.company_timezone) return;
  useAuthStore.setState({ activeTenant: { ...cur, company_timezone: res.company_timezone } });
}

// Đồng bộ ngôn ngữ 2 chiều quanh login:
//  • server → client: user.locale từ hồ sơ áp vào UI, NHƯNG chỉ khi user chưa tự chọn
//    trên máy này (applyServerLocale tôn trọng localStorage — lựa chọn tại chỗ thắng).
//  • client → server: user đã chọn ở màn login (trước khi có phiên) mà hồ sơ đang khác
//    → PATCH về meta.users.locale để thiết bị khác + BE (mail/push) theo đúng.
function syncLocaleAfterLogin(user) {
  try {
    const chosen = getLocale();
    applyServerLocale(user?.locale);
    if (user?.locale && chosen !== user.locale && getLocale() === chosen) {
      inboxClient.patch('/auth/me/locale', { locale: chosen }).catch(() => {});
    }
  } catch { /* i18n sync là best-effort, không được chặn login */ }
  // Múi giờ trình duyệt ⇒ hồ sơ ngay sau đăng nhập (effect lúc mount chỉ chạy khi tải trang đã có phiên — người vừa
  // gõ mật khẩu sẽ phải đợi lần tải trang sau). Body đăng nhập không mang timezone ⇒ đọc /auth/me. Không chặn login.
  inboxClient.get('/auth/me')
    .then((r) => syncDeviceTimezone(inboxClient, r.data?.user?.timezone))
    .then(apMuiWorkspaceVuaNhan)
    .catch(() => {});
}

// Debounced flush — gộp double-update vào 1 PUT/key. _timers + _pending shared module-level.
const _timers = {};
const _pending = {};

function setPreference(key, value) {
  // Optimistic update store: user.preferences[key] = value
  const store = useAuthStore.getState();
  const next = {
    ...(store.user || {}),
    preferences: { ...(store.user?.preferences || {}), [key]: value },
  };
  useAuthStore.setState({ user: next });

  _pending[key] = value;
  if (_timers[key]) clearTimeout(_timers[key]);
  _timers[key] = setTimeout(() => {
    const v = _pending[key];
    delete _pending[key];
    inboxClient.put('/auth/me/preferences', { [key]: v }).catch(() => {});
  }, 300);
}

// Flush pending preferences trước khi tab close — keepalive để complete sau unload.
if (typeof window !== 'undefined') {
  const flush = () => {
    const keys = Object.keys(_pending);
    if (!keys.length) return;
    try {
      const token = useAuthStore.getState().accessToken;
      const body = JSON.stringify({ ..._pending });
      // baseURL tuyệt đối: hub.cnvwork.com không proxy /api nên '/api/v1/...' tương đối chỉ chạm
      // nginx static của hub (404/405), preferences không bao giờ được lưu.
      fetch(`${baseURL}/auth/me/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body, keepalive: true,
      }).catch(() => {});
      for (const k of keys) delete _pending[k];
    } catch { /* ignore */ }
  };
  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);
}

export function AuthProvider({ children }) {
  const { user, accessToken, activeTenant, logout: doLogout } = useAuthStore();

  // Auto-refresh /auth/me KHI app mount (1 LẦN DUY NHẤT). Đảm bảo store cập
  // nhật role + private_modules mới nhất từ BE mà không cần user logout/login.
  //
  // QUAN TRỌNG (2026-05-27 fix sau bug burst 429):
  // - deps `[]` chứ KHÔNG `[accessToken]` — accessToken là string string, identity
  //   ổn định, nhưng nếu zustand persist re-hydrate gây 2 lần render thì cũng chỉ
  //   trigger 1 lần useEffect khi mount + sau khi state hydrate.
  // - SHALLOW COMPARE trước setState — chỉ update store NẾU data THẬT SỰ khác.
  //   Tránh tạo new object identity → trigger re-render → child useEffect([user])
  //   re-run → burst fetch (WebPushRegister, CnvChatIntegrationPage, etc.).
  useEffect(() => {
    const token = useAuthStore.getState().accessToken;
    if (!token) return;
    inboxClient.get('/auth/me').then((r) => {
      const fresh = r.data?.user;
      const freshTenant = r.data?.active_tenant;
      if (!fresh) return;
      // i18n: hồ sơ đổi locale từ thiết bị khác → tab này pickup khi mount.
      // applyServerLocale KHÔNG đè lựa chọn user đã tự chọn trên máy này.
      try { applyServerLocale(fresh.locale); } catch { /* best-effort */ }
      // Múi giờ trình duyệt ⇒ hồ sơ (và workspace còn giờ VN gieo sẵn ⇒ máy chủ nhận theo chủ/admin).
      syncDeviceTimezone(inboxClient, fresh.timezone).then(apMuiWorkspaceVuaNhan).catch(() => {});
      const cur = useAuthStore.getState().user || {};
      const curTenant = useAuthStore.getState().activeTenant || {};
      // Shallow compare các field quan trọng — KHÔNG setState nếu giống nhau,
      // để giữ identity object stable + tránh trigger child re-render.
      const userChanged = (
        cur.role !== fresh.role ||
        cur.name !== fresh.name ||
        cur.avatar !== fresh.avatar ||
        JSON.stringify(cur.permissions || []) !== JSON.stringify(fresh.permissions || []) ||
        JSON.stringify(cur.module_seats || {}) !== JSON.stringify(fresh.module_seats || {})
      );
      const tenantChanged = freshTenant && (
        curTenant.tenant_id !== freshTenant.tenant_id ||
        JSON.stringify(curTenant.private_modules || {}) !== JSON.stringify(freshTenant.private_modules || {}) ||
        JSON.stringify(curTenant.branding || null) !== JSON.stringify(freshTenant.branding || null) ||
        // Múi giờ workspace (12/10/2026): phiên lưu trước khi lên bản chưa có khoá này, admin đổi múi ở máy khác ⇒
        // pick up ở đây, lib/workspaceTimezone.js áp + dựng lại trang. Máy chủ cũ không trả ⇒ undefined ⇒ bỏ qua.
        (freshTenant.company_timezone !== undefined && curTenant.company_timezone !== freshTenant.company_timezone)
      );
      if (!userChanged && !tenantChanged) return;        // No-op
      useAuthStore.setState({
        user: userChanged
          ? { ...cur, ...fresh, preferences: fresh.preferences ?? cur.preferences ?? {} }
          : cur,
        activeTenant: tenantChanged ? { ...curTenant, ...freshTenant } : curTenant,
      });
    }).catch(() => { /* silent — auth interceptor sẽ handle 401 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Run once on mount

  // Map về shape Core's useAuth — Core không có activeTenant nhưng có thể bổ sung
  const value = {
    user,
    accessToken,                                  // expose để useChat (cnv-ui) dùng cho socket auth
    loading: false,
    isAuthenticated: !!accessToken,
    activeTenant,                                 // bonus cho SaaS
    // Đăng xuất phải REVOKE server-side: store.logout() chỉ xoá localStorage nên refresh token
    // vẫn sống tới 7 ngày (giờ là sliding → vô hạn) và vẫn hiện ở màn "Thiết bị đang đăng nhập".
    // best-effort: mạng lỗi thì vẫn xoá phiên local, không giữ user lại trong app.
    logout: async () => {
      const refreshToken = useAuthStore.getState().refreshToken;
      // Trình duyệt này thôi nhận Web Push (sự cố 21/10/2026 — đã thoát vẫn hiện nội dung tin).
      const pushOff = unsubscribeWebPush();
      if (refreshToken) {
        // Thao tác THOÁT không được phụ thuộc vào mạng. `_retryCount: 3` để request này bỏ qua
        // lưới auto-retry transient (3×3s) của interceptor, timeout ngắn, và chờ tối đa 3s rồi
        // vẫn xoá phiên local. '/auth/logout' cũng nằm trong AUTH_BROKEN_ALLOWLIST nên không
        // kích proactive refresh (ladder ~70s).
        const revoke = inboxClient
          .post('/auth/logout', { refreshToken }, { timeout: 5000, _retryCount: 3 })
          .catch(() => {});
        await Promise.race([Promise.all([revoke, pushOff]), new Promise((r) => { setTimeout(r, 3000); })]);
      } else {
        await Promise.race([pushOff, new Promise((r) => { setTimeout(r, 3000); })]);
      }
      doLogout();
    },
    setPreference,                                // Core MobileSection + Tasks/CRM filters dùng
    // Login methods — match HOST_CONTRACT.md. Dùng bởi @cnv/ui/pages/auth/LoginPage.
    login: async (email, password) => {
      const result = await authApi.login(email, password);
      useAuthStore.getState().setSession(result);
      resetAuthBroken();                            // Chunk B: re-enable inboxClient requests sau login lại
      syncLocaleAfterLogin(result.user);
      return result.user;
    },
    loginWithGoogle: async (arg) => {
      // arg: idToken (string) hoặc { code } (auth-code flow — kèm calendar scope).
      const result = await authApi.loginWithGoogle(arg);
      useAuthStore.getState().setSession(result);
      resetAuthBroken();
      syncLocaleAfterLogin(result.user);
      return result.user;
    },
    // Quên mật khẩu — dùng bởi @cnv/ui/pages/auth/{ForgotPasswordPage,ResetPasswordPage},
    // xem HOST_CONTRACT.md. Ba endpoint KHÔNG cần đăng nhập; `inboxClient` phải để lỗi
    // NGUYÊN VẸN (không nuốt) vì 2 trang kia đọc `err.response.status` (429) và
    // `err.response.data.error.details.issue` để hiện đúng câu.
    forgotPassword: async (email) => {
      await inboxClient.post('/auth/password/forgot', { email });
    },
    verifyResetToken: async (token) => {
      const r = await inboxClient.get('/auth/password/reset-token', { params: { token } });
      return r.data;
    },
    resetPassword: async (token, newPassword) => {
      await inboxClient.post('/auth/password/reset', { token, newPassword });
    },
    // Đổi ngôn ngữ TRONG app (menu user): kernel đã đổi UI + localStorage; đây chỉ
    // đồng bộ về hồ sơ để thiết bị khác / BE compose text đúng ngôn ngữ.
    changeLocale: async (code) => {
      await inboxClient.patch('/auth/me/locale', { locale: code });
    },
    listMyTenants: async () => {
      try {
        const tenants = await authApi.listMyTenants();
        return (tenants || []).filter((t) => t.status !== 'terminated');
      } catch {
        return [];
      }
    },
    // Re-fetch /auth/me → cập nhật user + activeTenant trong zustand store.
    // Khi tenant flag thay đổi (vd private_modules.team_chat=true) hoặc role
    // update, FE sẽ pickup mà KHÔNG cần logout/login.
    refreshUser: async () => {
      try {
        const r = await inboxClient.get('/auth/me');
        const fresh = r.data?.user;
        const freshTenant = r.data?.active_tenant;
        if (fresh) {
          const cur = useAuthStore.getState().user || {};
          const curTenant = useAuthStore.getState().activeTenant || {};
          useAuthStore.setState({
            user: { ...cur, ...fresh, preferences: fresh.preferences ?? cur.preferences ?? {} },
            // Merge tenant — preserve fields BE không trả (vd nếu /auth/me thiếu)
            activeTenant: freshTenant ? { ...curTenant, ...freshTenant } : curTenant,
          });
          return fresh;
        }
      } catch { /* */ }
      return null;
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

/** Hook tương thích với Core's useAuth(). */
export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    // Fallback — direct dùng store nếu Provider chưa wrap (ví dụ test isolated component)
    const { user, accessToken, activeTenant, logout } = useAuthStore.getState();
    return {
      user, accessToken, loading: false, isAuthenticated: !!accessToken, activeTenant, logout,
      setPreference,
      login: async () => null, loginWithGoogle: async () => null, refreshUser: async () => null,
    };
  }
  return ctx;
}
